import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  DollarSign, Phone, Mail, User, FileText, Search, 
  AlertCircle, CheckCircle, Clock, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OutstandingBalances = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchBalances = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cashier/outstanding-balances`);
      setBalances(response.data);
    } catch (error) {
      console.error('Failed to fetch outstanding balances:', error);
      toast.error('Failed to load outstanding balances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  const filteredBalances = balances.filter(b =>
    b.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.invoice_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalOutstanding = filteredBalances.reduce((sum, b) => sum + b.outstanding_amount, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD' }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft':
        return <FileText className="w-4 h-4 text-slate-500" />;
      case 'finalized':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'submitted':
        return <Clock className="w-4 h-4 text-purple-500" />;
      case 'partially_paid':
        return <AlertCircle className="w-4 h-4 text-amber-500" />;
      default:
        return <FileText className="w-4 h-4 text-slate-500" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      finalized: 'Finalized',
      submitted: 'Submitted',
      partially_paid: 'Partial'
    };
    return labels[status] || status;
  };

  return (
    <Layout>
      <div className="page-container">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Outstanding Balances</h1>
          <p className="text-slate-500">Patients with unpaid invoices for follow-up</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalOutstanding)}</p>
                <p className="text-sm text-slate-500">Total Outstanding</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <User className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{filteredBalances.length}</p>
                <p className="text-sm text-slate-500">Pending Invoices</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {balances.filter(b => b.invoice_status === 'finalized').length}
                </p>
                <p className="text-sm text-slate-500">Ready for Payment</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="card p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by patient name or invoice number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Balances List */}
        {loading ? (
          <div className="card p-8 text-center">
            <div className="text-slate-500">Loading...</div>
          </div>
        ) : filteredBalances.length === 0 ? (
          <div className="card p-8 text-center">
            <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
            <p className="text-slate-600 font-medium">No outstanding balances!</p>
            <p className="text-sm text-slate-500">All invoices are paid</p>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200 bg-slate-50">
                  <th className="text-left p-4 font-semibold text-slate-700">Patient</th>
                  <th className="text-left p-4 font-semibold text-slate-700 hidden md:table-cell">Contact</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Invoice</th>
                  <th className="text-center p-4 font-semibold text-slate-700">Status</th>
                  <th className="text-right p-4 font-semibold text-slate-700">Amount</th>
                  <th className="text-right p-4 font-semibold text-slate-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredBalances.map((balance) => (
                  <tr key={balance.invoice_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-500" />
                        </div>
                        <span className="font-medium text-slate-900">{balance.patient_name}</span>
                      </div>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <div className="space-y-1">
                        {balance.phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="w-3 h-3" />
                            <a href={`tel:${balance.phone}`} className="hover:text-blue-600">{balance.phone}</a>
                          </div>
                        )}
                        {balance.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="w-3 h-3" />
                            <a href={`mailto:${balance.email}`} className="hover:text-blue-600">{balance.email}</a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="font-mono text-sm text-slate-600">{balance.invoice_number}</span>
                    </td>
                    <td className="p-4 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                        {getStatusIcon(balance.invoice_status)}
                        {getStatusLabel(balance.invoice_status)}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <span className="font-bold text-red-600">{formatCurrency(balance.outstanding_amount)}</span>
                    </td>
                    <td className="p-4 text-right">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/billing/${balance.invoice_id}`)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OutstandingBalances;
